import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Modal } from '@/components/ui/Modal'
import {
  Download,
  Upload,
  Database,
  Clock,
  CheckCircle2,
  AlertCircle,
  FileJson,
  Settings2,
} from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/utils/cn'
import { formatDate } from '@/utils/format-date'
import api from '@/services/api'
import { getFriendlyError } from '@/utils/getFriendlyError'

interface BackupEntry {
  id: string
  filename: string
  size: number
  createdAt: string
  status: 'completed' | 'failed' | 'in_progress'
  kind: 'database' | 'config'
  type: string
  canRestore: boolean
  message?: string
}

interface BackupScheduleInfo {
  enabled: boolean
  cron: string
  hour?: number
  retentionDays: number
  timezone: string
  description: string
}

function normalizeBackup(raw: Record<string, unknown>): BackupEntry {
  return {
    id: String(raw.id),
    filename: String(raw.filename ?? 'backup'),
    size: Number(raw.size) || 0,
    createdAt: String(raw.createdAt ?? new Date().toISOString()),
    status: (raw.status as BackupEntry['status']) || 'completed',
    kind: raw.kind === 'config' ? 'config' : 'database',
    type: String(raw.type ?? raw.kind ?? 'database'),
    canRestore: Boolean(raw.canRestore),
    message: raw.message ? String(raw.message) : undefined,
  }
}

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => h)

export function BackupTab() {
  const [backups, setBackups] = useState<BackupEntry[]>([])
  const [schedule, setSchedule] = useState<BackupScheduleInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [creatingDb, setCreatingDb] = useState(false)
  const [creatingConfig, setCreatingConfig] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [savingSettings, setSavingSettings] = useState(false)
  const [formEnabled, setFormEnabled] = useState(true)
  const [formHour, setFormHour] = useState(3)
  const [formRetention, setFormRetention] = useState(7)

  const fetchBackups = async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/system/backups', {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
        params: { _ts: Date.now() },
      })
      const rows = Array.isArray(data?.data) ? data.data : []
      setBackups(rows.map((row: Record<string, unknown>) => normalizeBackup(row)))
      const sched = (data.schedule ?? null) as BackupScheduleInfo | null
      setSchedule(sched)
      if (sched) {
        setFormEnabled(sched.enabled)
        setFormHour(typeof sched.hour === 'number' ? sched.hour : 3)
        setFormRetention(sched.retentionDays)
      }
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao carregar backups'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void fetchBackups()
  }, [])

  const openSettings = () => {
    if (schedule) {
      setFormEnabled(schedule.enabled)
      setFormHour(typeof schedule.hour === 'number' ? schedule.hour : 3)
      setFormRetention(schedule.retentionDays)
    }
    setSettingsOpen(true)
  }

  const handleSaveSettings = async () => {
    setSavingSettings(true)
    try {
      const { data } = await api.patch('/system/backups/settings', {
        enabled: formEnabled,
        hour: formHour,
        retentionDays: formRetention,
      })
      const next = (data?.data ?? null) as BackupScheduleInfo | null
      setSchedule(next)
      toast.success('Configuração de backup salva')
      setSettingsOpen(false)
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao salvar configuração'))
    } finally {
      setSavingSettings(false)
    }
  }

  const handleCreateBackup = async (kind: 'database' | 'config') => {
    const setBusy = kind === 'database' ? setCreatingDb : setCreatingConfig
    setBusy(true)
    try {
      const { data } = await api.post('/system/backups', { kind })
      const result = data?.data
      const created = result?.backup
        ? normalizeBackup(result.backup as Record<string, unknown>)
        : null

      if (result?.warning) {
        toast.warning(String(result.warning))
      } else {
        toast.success(
          kind === 'database'
            ? 'Backup do banco criado e salvo no servidor.'
            : 'Snapshot de configuração exportado.',
        )
      }

      if (created) {
        setBackups((prev) => [created, ...prev.filter((b) => b.id !== created.id)])
      } else {
        await fetchBackups()
      }
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao criar backup'))
    } finally {
      setBusy(false)
    }
  }

  const handleDownloadBackup = async (backup: BackupEntry) => {
    try {
      const response = await api.get(`/system/backups/${backup.id}/download`, {
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', backup.filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success('Download iniciado')
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao baixar backup'))
    }
  }

  const handleRestoreBackup = async (backup: BackupEntry) => {
    if (!backup.canRestore) {
      toast.error('Apenas backups de configuração podem ser restaurados pela interface.')
      return
    }
    if (
      !window.confirm(
        `Restaurar "${backup.filename}"?\n\nSerão atualizados: aparência do sistema, metas globais e dados de configuração. Usuários e negócios não são alterados.`,
      )
    ) {
      return
    }
    try {
      await api.post(`/system/backups/${backup.id}/restore`)
      toast.success('Configurações restauradas com sucesso!')
      await fetchBackups()
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao restaurar backup'))
    }
  }

  const formatSize = (bytes: number) => {
    if (!bytes || Number.isNaN(bytes)) return '—'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const kindLabel = (kind: BackupEntry['kind']) =>
    kind === 'database' ? 'Banco (SQL)' : 'Configuração (JSON)'

  const scheduleHour = schedule?.hour ?? 3

  return (
    <div className='space-y-5'>
      {schedule?.enabled && (
        <div className='rounded-xl border border-accent/20 bg-accent/5 px-4 py-3 text-[12px] text-text-primary'>
          <p className='font-semibold text-accent'>Backup automático ativo</p>
          <p className='mt-1 text-text-muted'>
            O banco é copiado todos os dias às{' '}
            <strong>{String(scheduleHour).padStart(2, '0')}:00</strong> ({schedule.timezone}).
            Backups com mais de <strong>{schedule.retentionDays} dias</strong> são excluídos
            automaticamente (arquivo e registro).
          </p>
        </div>
      )}

      {!schedule?.enabled && schedule && (
        <div className='rounded-xl border border-[var(--glass-border)] bg-[var(--glass-bg)] px-4 py-3 text-[12px] text-text-muted'>
          Backup automático desligado. Use <strong>Configurar backups</strong> para ativar agenda e retenção.
        </div>
      )}

      <div className='glass-card flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex items-center gap-3'>
          <Database size={20} className='text-[var(--brand-primary)]' />
          <div>
            <h3 className='text-sm font-semibold text-[var(--font-color-h3)]'>Backup do Sistema</h3>
            <p className='max-w-xl text-[12px] text-[var(--font-color-muted)]'>
              <strong>Banco:</strong> dump PostgreSQL salvo em{' '}
              <code className='text-[11px]'>server/backups/</code>.{' '}
              <strong>Exportar configuração:</strong> JSON com tema, metas e usuários (restaurável).
            </p>
          </div>
        </div>
        <div className='flex flex-wrap gap-2'>
          <Button
            type='button'
            variant='secondary'
            size='md'
            icon={<Settings2 size={14} />}
            onClick={openSettings}
          >
            Configurar backups
          </Button>
          <Button
            type='button'
            variant='primary'
            size='md'
            icon={<Database size={14} />}
            loading={creatingDb}
            onClick={() => void handleCreateBackup('database')}
          >
            {creatingDb ? 'Gerando...' : 'Backup do banco'}
          </Button>
          <Button
            type='button'
            variant='secondary'
            size='md'
            icon={<FileJson size={14} />}
            loading={creatingConfig}
            onClick={() => void handleCreateBackup('config')}
          >
            {creatingConfig ? 'Gerando...' : 'Exportar configuração'}
          </Button>
        </div>
      </div>

      <div className='glass-card overflow-hidden p-0'>
        <div className='border-b border-[var(--glass-border)] px-4 py-3'>
          <h3 className='text-sm font-semibold text-[var(--font-color-h3)]'>Backups realizados</h3>
        </div>

        {loading ? (
          <div className='flex items-center justify-center py-12'>
            <div className='h-5 w-5 animate-spin rounded-full border-2 border-[var(--brand-primary)]/30 border-t-[var(--brand-primary)]' />
          </div>
        ) : backups.length === 0 ? (
          <div className='py-12 text-center'>
            <Database size={32} className='mx-auto mb-2 opacity-50 text-[var(--font-color-muted)]' />
            <p className='text-sm italic text-[var(--font-color-muted)]'>Nenhum backup encontrado.</p>
          </div>
        ) : (
          <div className='divide-y divide-[var(--glass-border)]'>
            {backups.map((backup) => (
              <div
                key={backup.id}
                className='flex flex-col gap-3 px-4 py-3 transition-colors hover:bg-[var(--glass-bg)] sm:flex-row sm:items-center sm:justify-between'
              >
                <div className='flex min-w-0 items-center gap-3'>
                  <div
                    className={cn(
                      'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                      backup.status === 'completed'
                        ? 'bg-emerald-500/10'
                        : backup.status === 'failed'
                          ? 'bg-red-500/10'
                          : 'bg-blue-500/10',
                    )}
                  >
                    {backup.status === 'completed' ? (
                      <CheckCircle2 size={16} className='text-emerald-400' />
                    ) : backup.status === 'failed' ? (
                      <AlertCircle size={16} className='text-red-400' />
                    ) : (
                      <div className='h-4 w-4 animate-spin rounded-full border-2 border-blue-400/30 border-t-blue-400' />
                    )}
                  </div>
                  <div className='min-w-0'>
                    <p className='truncate text-[13px] font-medium text-[var(--font-color-body)]'>
                      {backup.filename}
                    </p>
                    <div className='flex flex-wrap items-center gap-2 text-[11px] text-[var(--font-color-muted)]'>
                      <span>{formatSize(backup.size)}</span>
                      <span>·</span>
                      <Clock size={10} />
                      <span>{formatDate(backup.createdAt)}</span>
                      <Badge variant={backup.kind === 'database' ? 'info' : 'muted'}>
                        {kindLabel(backup.kind)}
                      </Badge>
                      {backup.message?.includes('automático') && (
                        <Badge variant='muted'>Agendado</Badge>
                      )}
                    </div>
                    {backup.message && (
                      <p className='mt-0.5 truncate text-[10px] text-[var(--font-color-muted)]'>
                        {backup.message}
                      </p>
                    )}
                  </div>
                </div>
                <div className='flex items-center gap-1 sm:shrink-0'>
                  <button
                    type='button'
                    onClick={() => void handleDownloadBackup(backup)}
                    className='flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md p-2 text-[var(--text-muted)] transition-colors hover:bg-border-subtle hover:text-[var(--brand-primary)]'
                    title='Download'
                  >
                    <Download size={14} />
                  </button>
                  <button
                    type='button'
                    onClick={() => void handleRestoreBackup(backup)}
                    disabled={!backup.canRestore}
                    className={cn(
                      'flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md p-2 transition-colors',
                      backup.canRestore
                        ? 'text-[var(--text-muted)] hover:bg-border-subtle hover:text-emerald-500'
                        : 'cursor-not-allowed opacity-40',
                    )}
                    title={
                      backup.canRestore
                        ? 'Restaurar configuração'
                        : 'Restauração SQL apenas via script no servidor'
                    }
                  >
                    <Upload size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        title='Configurar backups'
        description='Agenda e retenção do backup automático do banco de dados.'
        size='md'
        footer={
          <div className='flex justify-end gap-2'>
            <Button type='button' variant='secondary' size='sm' onClick={() => setSettingsOpen(false)}>
              Cancelar
            </Button>
            <Button
              type='button'
              variant='primary'
              size='sm'
              loading={savingSettings}
              onClick={() => void handleSaveSettings()}
            >
              Salvar
            </Button>
          </div>
        }
      >
        <div className='space-y-4'>
          <label className='flex items-center justify-between gap-3 rounded-lg border border-[var(--glass-border)] px-3 py-3'>
            <div>
              <p className='text-sm font-medium text-text-primary'>Backup automático</p>
              <p className='text-[11px] text-text-muted'>Liga ou desliga o job diário no servidor</p>
            </div>
            <input
              type='checkbox'
              checked={formEnabled}
              onChange={(e) => setFormEnabled(e.target.checked)}
              className='h-5 w-5 accent-[var(--brand-primary)]'
            />
          </label>

          <div className='space-y-1.5'>
            <label htmlFor='backup-hour' className='text-xs font-medium text-text-primary'>
              Horário (America/Sao_Paulo)
            </label>
            <select
              id='backup-hour'
              value={formHour}
              onChange={(e) => setFormHour(Number(e.target.value))}
              className='w-full rounded-md border border-[var(--glass-border)] bg-bg-main px-3 py-2 text-sm text-text-primary'
            >
              {HOUR_OPTIONS.map((h) => (
                <option key={h} value={h}>
                  {String(h).padStart(2, '0')}:00
                </option>
              ))}
            </select>
          </div>

          <div className='space-y-1.5'>
            <label htmlFor='backup-retention' className='text-xs font-medium text-text-primary'>
              Retenção (dias)
            </label>
            <input
              id='backup-retention'
              type='number'
              min={1}
              max={90}
              value={formRetention}
              onChange={(e) => setFormRetention(Number(e.target.value) || 7)}
              className='w-full rounded-md border border-[var(--glass-border)] bg-bg-main px-3 py-2 text-sm text-text-primary'
            />
            <p className='text-[11px] text-text-muted'>
              Backups mais antigos que este período são removidos automaticamente.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  )
}
